import { cloneDeep, pick } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Loading from '../../common/components/Loading';
import { FormContext } from '../../common/form/FormContext';
import FormModal from '../../common/form/FormModal';
import I18n, { useI18n } from '../../common/i18n/I18n';
import { ACTIVITY_CONTEXT_SOURCES } from '../../scheduler/schedulerConstants';
import AddProjectOwnerModal, { doSavePerson } from '../form/production/AddProjectOwnerModal';
import { EMPTY_ACTIVITY } from '../helpers/activityUtils';
import { extractNames } from '../helpers/peopleUtils';
import { useActiviesData } from '../helpers/productionApi';
import { EMPTY_PERSON_MODAL_STATE } from '../helpers/productionUtils';
import { ActivityFormItems } from './ActivityFormItems';

export default function ActivityEditor({
    isOpen,
    setIsOpen = undefined,
    onClose,
    handleSave,
    isLoading,
    isSaving,
    resourceOptions = undefined,
    productionId = undefined,
    activityId = undefined,
    showBasic,
    isInModal,
    setIsSaveDisabled,
    isResourceForm,
    sourceContextType = undefined,
    hiddenFields = undefined,
    disabledFields = undefined,
    hasError = false
}) {
    const { validation, state, setState } = useContext(FormContext);
    const { activitiesData, activitiesLoading } = useActiviesData(productionId);
    const [personModalState, setPersonModalState] = useState(EMPTY_PERSON_MODAL_STATE);
    const [isPersonSaveDisabled, setIsPersonSaveDisabled] = useState(true);
    const guestEditorRef = useRef(null);
    const [isFormSaving, setIsFormSaving] = useState(false);
    const t = useI18n();

    const activityOptions = useMemo(() => {
        if (activitiesData?.results?.hits?.items) {
            return activitiesData.results.hits.items
                .filter((item) => item.id !== activityId)
                .map((item) => {
                    return { id: item.id, label: item.name };
                });
        }
    }, [activitiesData?.results?.hits?.items, activityId]);

    const mergedSaveDisabled =
        (validation && !validation.isValid) || (personModalState.open ? isPersonSaveDisabled : isSaving);
    useEffect(() => {
        setIsSaveDisabled?.(mergedSaveDisabled);
    }, [mergedSaveDisabled, setIsSaveDisabled]);

    const onCloseActivityEditor = () => {
        setState({ ...cloneDeep(EMPTY_ACTIVITY) });
        onClose();
        setIsOpen?.(false);
    };

    const onSave = () => {
        if (personModalState.open)
            doSavePerson(
                setIsFormSaving,
                (input) => {
                    setState((prev) => ({
                        ...prev,
                        activityOwner: pick(input.personReference, [
                            'id',
                            'label',
                            'type',
                            'eventId',
                            'uriPrefix'
                        ])
                    }));
                },
                guestEditorRef,
                setPersonModalState
            );
        else {
            handleSave();
            onCloseActivityEditor();
        }
    };

    const onCreateOwner = (e) => {
        const isEmail = e.label?.includes('@');
        setIsPersonSaveDisabled(true);

        setPersonModalState({
            open: true,
            prepopulate: {
                name: isEmail ? { firstName: '', lastName: '' } : extractNames(e.label),
                email: isEmail ? e.label : ''
            }
        });
    };

    const additionalLeftsideActions = personModalState.open
        ? [
              {
                  label: t('import.contacts.uploadfile.modal.back'),
                  color: 'inherit' as const,
                  onClick: () => {
                      setPersonModalState(EMPTY_PERSON_MODAL_STATE);
                  }
              }
          ]
        : [];

    return isInModal ? (
        <FormModal
            isOpen={isOpen}
            width="700px"
            onClose={onCloseActivityEditor}
            title={
                personModalState.open ? (
                    <I18n token="form.shortlist.guestEditor" />
                ) : activityId ? (
                    <I18n token="production.activity.edit" />
                ) : (
                    <I18n token="production.activity.new" />
                )
            }
            id={`production-schedule`}
            isSaving={isFormSaving}
            hideDefaultButtons={true}
            additionalActions={[
                !personModalState.open && {
                    label: t('dialog.cancel'),
                    onClick: () => {
                        onCloseActivityEditor();
                    },
                    variant: 'text'
                },
                {
                    label: t('dialog.save'),
                    onClick: () => {
                        onSave();
                    },
                    variant: 'contained',
                    disabled: personModalState.open
                        ? isPersonSaveDisabled || isFormSaving
                        : mergedSaveDisabled
                }
            ]}
            additionalLeftsideActions={additionalLeftsideActions}
        >
            {isLoading ? (
                <Loading />
            ) : personModalState.open ? (
                <AddProjectOwnerModal
                    personModalState={personModalState}
                    setIsSaveDisabled={setIsPersonSaveDisabled}
                    guestEditorRef={guestEditorRef}
                />
            ) : sourceContextType === ACTIVITY_CONTEXT_SOURCES.RESERVATION ? (
                <ActivityFormItems
                    showBasic={showBasic}
                    activitiesLoading={activitiesLoading}
                    activityOptions={activityOptions}
                    isResourceForm={isResourceForm}
                    hiddenFields={hiddenFields}
                    hasError={hasError}
                />
            ) : (
                <ActivityFormItems
                    showBasic={showBasic}
                    resourceOptions={resourceOptions}
                    activitiesLoading={activitiesLoading}
                    activityOptions={activityOptions}
                    isResourceForm={isResourceForm}
                    onCreateOwner={onCreateOwner}
                    hiddenFields={hiddenFields}
                    disabledFields={disabledFields}
                />
            )}
        </FormModal>
    ) : (
        <>
            {isLoading ? (
                <Loading />
            ) : personModalState.open ? (
                <AddProjectOwnerModal
                    personModalState={personModalState}
                    setIsSaveDisabled={setIsPersonSaveDisabled}
                    guestEditorRef={guestEditorRef}
                />
            ) : (
                <ActivityFormItems
                    showBasic={showBasic}
                    resourceOptions={resourceOptions}
                    activitiesLoading={activitiesLoading}
                    activityOptions={activityOptions}
                    isResourceForm={isResourceForm}
                    onCreateOwner={onCreateOwner}
                    hiddenFields={hiddenFields}
                />
            )}
        </>
    );
}
